import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useRedirect from 'src/hooks/useRedirect';
import useGetExternalCatalogMutation, {
  ExternalCatalogResponse,
} from 'src/services/catalog_products/useGetExternalCatalogMutation';
import {
  clearPunchoutPendingRedirect,
  getPunchoutPendingRedirect,
  PUNCHOUT_EXTERNAL_REDIRECT_DELAY_MS,
  setPunchoutPendingRedirect,
  shouldShowPunchoutSessionInfo,
} from './punchoutRedirectUtils';

interface UsePunchoutExternalSiteRedirectOptions {
  errorMessage: string;
  // Called instead of the errorMessage toast when the API answers but cannot open the punchout.
  // Request failures (no response body to show) still toast errorMessage.
  onFailure?: (response: ExternalCatalogResponse) => void;
}

export const usePunchoutExternalSiteRedirect = ({
  errorMessage,
  onFailure,
}: UsePunchoutExternalSiteRedirectOptions) => {
  const navigate = useNavigate();
  const { getUrl } = useRedirect();
  const {
    mutateAsync: fetchExternalCatalog,
    isPending,
    variables: openingCatalogId,
  } = useGetExternalCatalogMutation();

  const openPunchout = useCallback(
    async (catalogId: number) => {
      try {
        const data = await fetchExternalCatalog(catalogId);
        if (data.statusCode !== 200 || !data.redirectUrl) {
          if (onFailure) {
            onFailure(data);
          } else {
            toast.error(errorMessage);
          }
          return;
        }

        setPunchoutPendingRedirect(catalogId, data.redirectUrl, data.showPunchoutOrderGuide);
        navigate(getUrl('punchoutOrderGuide')(String(catalogId)));
      } catch (error) {
        console.error(error);
        toast.error(errorMessage);
      }
    },
    [errorMessage, fetchExternalCatalog, getUrl, navigate, onFailure]
  );

  return {
    openPunchout,
    isPending,
    openingCatalogId,
  };
};

export const usePunchoutOrderGuideExternalRedirect = (catalogId: number) => {
  const [showSessionInfo] = useState(() => shouldShowPunchoutSessionInfo(catalogId));

  useEffect(() => {
    const redirectUrl = getPunchoutPendingRedirect(catalogId);
    if (!redirectUrl) {
      return;
    }

    const timer = window.setTimeout(() => {
      window.open(redirectUrl);
      clearPunchoutPendingRedirect();
    }, PUNCHOUT_EXTERNAL_REDIRECT_DELAY_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [catalogId]);

  return { showSessionInfo };
};
